import React, { FC, useRef } from 'react';

import {
  Box,
  Flex,
  Modal,
  Button,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';

import { useIsSuperAdmin } from 'store/slices';

import { Form, Formik } from 'formik';

import FormikInput from 'components/primitives/FormikInput';
import FormikSelectInput from 'components/primitives/FormikSelectInput';

import {
  rolesMap,
  initialValues,
  validationSchema,
} from './AddEmployeeModal.utils';

import { RolesEnum } from 'types/user';
import { AddEmployeeModalViewProps } from './AddEmployeeModal.props';
import getSortedData from 'utils/getSortedData';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
};

const normalRoles = [RolesEnum.WASH_HUB_STAFF, RolesEnum.WASH_HUB_ADMIN];
const allRoles = [
  RolesEnum.WASH_HUB_STAFF,
  RolesEnum.WASH_HUB_ADMIN,
  RolesEnum.SUPER_ADMIN,
];

const AddEmployeeModalView: FC<AddEmployeeModalViewProps> = (props) => {
  const isSuperAdmin = useIsSuperAdmin();

  const roles = isSuperAdmin ? allRoles : normalRoles;

  const cancelRef = useRef(null);
  const disclosure = useDisclosure();

  const { isOpen, onClose, onCreate, isLoading } = props;

  return (
    <Modal
      size="6xl"
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="30px">
        <ModalCloseButton _focus={{ outline: 'none' }} />
        <ModalHeader textAlign="center" fontWeight="500">
          Add employee
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" p={6}>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onCreate}>
              {({ isValid, values, handleSubmit }) => {
                const isSuperAdminSelected =
                  values?.role === RolesEnum.SUPER_ADMIN;

                return (
                  <Form>
                    <Flex>
                      <FormikInput
                        name="fname"
                        label="First Name (*)"
                        labelProps={labelProps}
                        placeholder="Your text here"
                      />
                      <Box h="20px" w="50px" />
                      <FormikInput
                        name="lname"
                        label="Last Name (*)"
                        labelProps={labelProps}
                        placeholder="Your text here"
                      />
                    </Flex>
                    <Flex mt={4}>
                      <FormikSelectInput
                        label="Role (*)"
                        name="role"
                        placeholder="Select a role">
                        {getSortedData(roles).map((r) => (
                          <option value={r} key={r}>
                            {rolesMap[r]}
                          </option>
                        ))}
                      </FormikSelectInput>
                      <Box h="20px" w="50px" />
                      <FormikInput
                        name="phone"
                        label="Phone Number"
                        labelProps={labelProps}
                        placeholder="Your text here"
                      />
                    </Flex>
                    <Flex mt={4} maxW="390px">
                      <FormikInput
                        name="email"
                        label="Email (*)"
                        labelProps={labelProps}
                        placeholder="Your text here"
                      />
                    </Flex>
                    <Flex my={12} flexDirection="row" justifyContent="center">
                      <Button
                        variant="outline"
                        marginRight="10px"
                        isDisabled={isLoading}
                        w="134px"
                        onClick={onClose}>
                        Cancel
                      </Button>
                      <Button
                        type={isSuperAdminSelected ? 'button' : 'submit'}
                        isDisabled={!isValid}
                        isLoading={isLoading}
                        w="134px"
                        onClick={
                          isSuperAdminSelected ? disclosure.onOpen : undefined
                        }>
                        Create
                      </Button>
                      {isSuperAdminSelected && (
                        <AlertDialog
                          isCentered
                          isOpen={disclosure.isOpen}
                          leastDestructiveRef={cancelRef}
                          onClose={disclosure.onClose}>
                          <AlertDialogOverlay>
                            <AlertDialogContent>
                              <AlertDialogHeader
                                fontSize="lg"
                                fontWeight="bold">
                                Info
                              </AlertDialogHeader>

                              <AlertDialogBody>
                                Super Admins have access to all Wash Hubs.
                              </AlertDialogBody>

                              <AlertDialogFooter>
                                <Button
                                  variant="outline"
                                  ref={cancelRef}
                                  onClick={() => {
                                    disclosure.onClose();
                                    onClose();
                                  }}>
                                  Cancel
                                </Button>
                                <Button
                                  onClick={() => {
                                    handleSubmit();
                                    disclosure.onClose();
                                  }}
                                  colorScheme="red"
                                  ml={3}>
                                  Create
                                </Button>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialogOverlay>
                        </AlertDialog>
                      )}
                    </Flex>
                  </Form>
                );
              }}
            </Formik>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default AddEmployeeModalView;
